/**
 * Vendor-neutral display copy for play/node types.
 *
 * The backend still names the LinkedIn play after the underlying vendor
 * ("HeyReach Campaign", "... via HeyReach automation platform"), but users only
 * ever think of it as LinkedIn outreach. Every surface that renders a
 * server-supplied play name or description routes through here so the vendor
 * name never reaches the UI.
 *
 * Display layer only — persisted `action_type` values (`heyreach_*`), API
 * payloads, and form field names are untouched.
 */

export const LINKEDIN_OUTREACH_LABEL = 'LinkedIn Outreach';

const LINKEDIN_OUTREACH_DESCRIPTION =
  'Add qualified leads to LinkedIn outreach campaigns — one LinkedIn touch per node.';

const PLAY_NAME_OVERRIDES: Record<string, string> = {
  heyreach_campaign: LINKEDIN_OUTREACH_LABEL,
};

const PLAY_DESCRIPTION_OVERRIDES: Record<string, string> = {
  heyreach_campaign: LINKEDIN_OUTREACH_DESCRIPTION,
};

/**
 * Rewrites vendor mentions in copy we don't control (legacy granular nodes,
 * whose names the backend composes per action_type). No-op for every other play.
 */
export function stripVendorName(text?: string | null): string {
  if (!text) return '';
  return text
    .replace(/\s*\bvia\s+HeyReach(\s+automation\s+platform)?/gi, ' via LinkedIn automation')
    .replace(/\bHeyReach\b/gi, LINKEDIN_OUTREACH_LABEL)
    // Collapse the "LinkedIn Outreach LinkedIn ..." that the substitution above
    // produces for names already carrying "LinkedIn".
    .replace(new RegExp(`${LINKEDIN_OUTREACH_LABEL}\\s+LinkedIn\\b`, 'gi'), LINKEDIN_OUTREACH_LABEL)
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** Display name for a node/play, given the server-supplied name. */
export function getPlayDisplayName(actionType?: string | null, name?: string | null): string {
  if (actionType && PLAY_NAME_OVERRIDES[actionType]) return PLAY_NAME_OVERRIDES[actionType];
  return stripVendorName(name);
}

/** Display description for a node/play, given the server-supplied description. */
export function getPlayDisplayDescription(
  actionType?: string | null,
  description?: string | null,
): string {
  if (actionType && PLAY_DESCRIPTION_OVERRIDES[actionType]) {
    return PLAY_DESCRIPTION_OVERRIDES[actionType];
  }
  return stripVendorName(description);
}
